// Application Insights wrapper for the admin SPA.
//
// The instrumentation key is injected at build time by the pipeline as
// VITE_APPINSIGHTS_INSTRUMENTATIONKEY. When it is absent (local dev)
// every function below is a no-op, so the app runs without telemetry.
//
// Per docs/plans/admin-site-proposal.md section 5 the admin site reuses
// the shared App Insights component; all admin telemetry is tagged with
// app="admin" so admin traffic can be separated from the public site.

let appInsights = null;
let initialized = false;

/**
 * Initialize the SDK (dynamic import so the telemetry bundle is only
 * loaded when an instrumentation key is present).
 */
export async function initTelemetry() {
  if (initialized) return appInsights;
  initialized = true;

  const key = import.meta.env.VITE_APPINSIGHTS_INSTRUMENTATIONKEY;
  if (!key) {
    console.info('[telemetry] No instrumentation key set — telemetry disabled.');
    return null;
  }

  try {
    const { ApplicationInsights } = await import('@microsoft/applicationinsights-web');
    appInsights = new ApplicationInsights({
      config: {
        instrumentationKey: key,
        autoCollectPageViews: true,
        enableAutoRouteTracking: true,
      },
    });
    appInsights.loadAppInsights();

    // Tag all telemetry so admin traffic is distinguishable from the
    // public site in the shared App Insights component.
    const config = appInsights.telemetryConfiguration || appInsights.config;
    if (config?.setGlobalContextProperty) {
      config.setGlobalContextProperty('app', 'admin');
    }
    return appInsights;
  } catch (err) {
    console.warn('[telemetry] Failed to initialize Application Insights', err);
    appInsights = null;
    return null;
  }
}

/** Set the authenticated user on the telemetry context (no-op in dev). */
export function setAuthenticatedUserContext(userId) {
  try {
    appInsights?.setAuthenticatedUserContext?.(userId || 'anonymous');
  } catch {
    // Never let telemetry break the app.
  }
}

/** Track a custom event with the admin app tag. */
export function trackEvent(name, properties = {}) {
  try {
    appInsights?.trackEvent?.({ name, properties: { app: 'admin', ...properties } });
  } catch {
    // no-op
  }
}

/** Track a page view with the admin app tag. */
export function trackPageView(name, properties = {}) {
  try {
    appInsights?.trackPageView?.({ name, properties: { app: 'admin', ...properties } });
  } catch {
    // no-op
  }
}
